import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, Plane, XCircle } from "lucide-react";
import { toast } from "sonner";

const dates = (o) => (o.from_date === o.to_date ? o.from_date : `${o.from_date} → ${o.to_date}`);
const span = (o) => (o.half_day ? "Half day" : `${o.days} day${o.days > 1 ? "s" : ""}`);

export default function OnDuty() {
  const qc = useQueryClient();
  const { data: pending, isLoading } = useQuery({
    queryKey: ["onDutyPendingHR"],
    queryFn: async () => (await api.get("/on-duty", { params: { status: "Pending_HR" } })).data,
  });
  const { data: all } = useQuery({ queryKey: ["onDutyAll"], queryFn: async () => (await api.get("/on-duty")).data });

  const decide = useMutation({
    mutationFn: async ({ id, action }) => {
      const remarks = window.prompt(action === "approve" ? "Remarks (optional)" : "Reason for not approving (optional)") ?? null;
      if (remarks === null) throw new Error("cancelled");
      return (await api.post(`/on-duty/${id}/decide`, { action, remarks })).data;
    },
    onSuccess: (d) => {
      toast.success(d.status === "Approved" ? "On Duty approved — attendance updated" : "On Duty rejected");
      qc.invalidateQueries({ queryKey: ["onDutyPendingHR"] }); qc.invalidateQueries({ queryKey: ["onDutyAll"] });
    },
    onError: (e) => { if (e.message !== "cancelled") toast.error(apiError(e.response?.data?.detail)); },
  });

  return (
    <div>
      <PageHeader overline="Attendance" title="On Duty" icon={Plane}
        subtitle="Official tour, external training and official meetings. Employee → Reporting Officer (recommendation) → HR (final approval). Approved days count as present and paid; any Half Day deduction on them is reversed. On Duty does not earn comp-off." />
      <Tabs defaultValue="pending">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending">Pending Final Approval ({pending?.length || 0})</TabsTrigger>
          <TabsTrigger value="all">All Requests ({all?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {isLoading ? <Loading /> : (pending || []).length === 0 ? (
            <EmptyState icon={Plane} title="No On Duty requests awaiting HR approval" hint="Requests recommended by a Reporting Officer (or with no RO in the chain) appear here." />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Type</TableHead>
                  <TableHead className="overline text-slate-500">Dates</TableHead>
                  <TableHead className="overline text-slate-500">Place & Purpose</TableHead>
                  <TableHead className="overline text-slate-500">RO Recommendation</TableHead>
                  <TableHead className="text-right overline text-slate-500">Decision</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {pending.map((o) => (
                    <TableRow key={o.id} className="table-row-i">
                      <TableCell className="font-medium text-navy">{o.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{o.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{o.od_type}</TableCell>
                      <TableCell className="text-slate-600 text-sm whitespace-nowrap">{dates(o)}<span className="block text-xs text-slate-400">{span(o)}</span></TableCell>
                      <TableCell className="text-slate-600 text-sm min-w-[220px] max-w-[360px] whitespace-normal break-words"><span className="font-medium text-navy block">{o.place}</span><span className="text-xs text-slate-500">{o.purpose}</span></TableCell>
                      <TableCell className="text-slate-500 text-xs max-w-[180px]">{o.ro_decided_by ? <>Recommended by {o.ro_name || o.ro_decided_by}{o.ro_remarks ? ` — ${o.ro_remarks}` : ""}</> : <span className="text-slate-300">No reporting officer in chain</span>}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" title="Approve" onClick={() => decide.mutate({ id: o.id, action: "approve" })}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" title="Reject" onClick={() => decide.mutate({ id: o.id, action: "reject" })}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          {(all || []).length === 0 ? (
            <EmptyState icon={Plane} title="No On Duty requests yet" />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Type</TableHead>
                  <TableHead className="overline text-slate-500">Dates</TableHead>
                  <TableHead className="overline text-slate-500">Place</TableHead>
                  <TableHead className="overline text-slate-500">Status</TableHead>
                  <TableHead className="overline text-slate-500">Remarks</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {all.map((o) => (
                    <TableRow key={o.id} className="table-row-i">
                      <TableCell className="font-medium text-navy">{o.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{o.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{o.od_type}</TableCell>
                      <TableCell className="text-slate-600 text-sm whitespace-nowrap">{dates(o)}<span className="block text-xs text-slate-400">{span(o)}</span></TableCell>
                      <TableCell className="text-slate-600 text-sm">{o.place}</TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                      <TableCell className="text-slate-500 text-xs min-w-[160px] max-w-[300px] whitespace-normal break-words">{o.remarks || o.ro_remarks || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
